import React, { useCallback, useEffect, useRef, useState } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { Empty, Skeleton } from "antd";
import DebounceInput from "../../../commonComponents/DebounceInput";
import CustomButton from "../../../commonComponents/button";
import KlipyLogo from "../../../../icons/svg/klipy-logo.svg";

const KLIPY_BASE_URL = "https://api.klipy.com/api/v1";
const PER_PAGE = 24;

// A persistent per-customer id is required by Klipy for ad/recents tracking.
// Reuse a stable random id stored locally so the same browser maps to one customer.
const getCustomerId = () => {
  try {
    let id = localStorage.getItem("klipyCustomerId");
    if (!id) {
      id = `web-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem("klipyCustomerId", id);
    }
    return id;
  } catch {
    return "web-anonymous";
  }
};

const TRENDING = "__trending__";

const GifPickerComponent = ({ attachments, setAttachments }) => {
  const apiKey = process.env.GATSBY_GIF_API_KEY || "";
  const [search, setSearch] = useState("");
  // null => category grid view. A label string => GIF results view.
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const customerId = useRef(getCustomerId());
  const scrollRef = useRef(null);
  // Pagination state for the active GIF query. Held in a ref so the scroll
  // handler always reads the latest values without re-binding.
  const pagingRef = useRef({ query: null, page: 1, hasNext: false, loading: false });

  const scrollToTop = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const fetchCategories = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    try {
      const res = await fetch(`${KLIPY_BASE_URL}/${apiKey}/gifs/categories`);
      const json = await res.json();
      setCategories(json?.data?.categories || []);
    } catch (err) {
      console.error("Failed to fetch GIF categories from Klipy", err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const fetchGifs = useCallback(
    async (query, page = 1, append = false) => {
      if (!apiKey) {
        setGifs([]);
        return;
      }
      pagingRef.current.loading = true;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const trimmed = (query || "").trim();
        const base =
          trimmed && trimmed !== TRENDING
            ? `${KLIPY_BASE_URL}/${apiKey}/gifs/search?q=${encodeURIComponent(trimmed)}`
            : `${KLIPY_BASE_URL}/${apiKey}/gifs/trending?`;
        const endpoint = `${base}&page=${page}&per_page=${PER_PAGE}&customer_id=${customerId.current}`;
        const res = await fetch(endpoint);
        const json = await res.json();
        const items = json?.data?.data || [];
        setGifs(prev => (append ? [...prev, ...items] : items));
        pagingRef.current = {
          query,
          page,
          hasNext: Boolean(json?.data?.has_next),
          loading: false,
        };
      } catch (err) {
        console.error("Failed to fetch GIFs from Klipy", err);
        if (!append) setGifs([]);
        pagingRef.current.loading = false;
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [apiKey]
  );

  const loadMore = useCallback(() => {
    const { query, page, hasNext, loading: paging } = pagingRef.current;
    if (paging || !hasNext) return;
    fetchGifs(query, page + 1, true);
  }, [fetchGifs]);

  const handleScroll = e => {
    const el = e.currentTarget;
    // Trigger a little before the very bottom for a smoother experience.
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadMore();
    }
  };

  // Load the category grid on mount.
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // DebounceInput already debounces and passes the final value to onChange.
  const handleSearch = value => {
    setSearch(value);
    if (value.trim()) {
      setActiveCategory(value.trim());
      fetchGifs(value);
    } else {
      // Empty search returns to the category grid.
      setActiveCategory(null);
      setGifs([]);
    }
  };

  const openCategory = (label, query) => {
    setActiveCategory(label);
    setSearch(query === TRENDING ? "" : query);
    fetchGifs(query);
    scrollToTop();
  };

  const goBackToCategories = () => {
    setActiveCategory(null);
    setSearch("");
    setGifs([]);
    scrollToTop();
  };

  const createMessage = gif => {
    // Prefer the medium gif rendition for sharing, fall back through the chain.
    const file = gif?.file || {};
    const gifRendition = file?.md?.gif || file?.hd?.gif || file?.sm?.gif || {};
    if (!gifRendition.url) return;
    setAttachments([
      ...attachments,
      {
        file: {
          name: gif.title || gif.slug || "gif",
          type: "image/gif",
          size: gifRendition.size || 0,
        },
        preview: gifRendition.url,
        generateLink: false,
      },
    ]);
  };

  const isCategoryView = activeCategory === null;

  return (
    <div className="w-[320px]">
      {isCategoryView ? (
        <DebounceInput
          allowClear
          placeholder="Search KLIPY"
          value={search}
          onChange={handleSearch}
        />
      ) : (
        <div className="flex items-center gap-2">
          <CustomButton
            type="text"
            icon={<LeftOutlined />}
            handleClick={goBackToCategories}
            tooltipTitle="Back to categories"
          />
          <DebounceInput
            allowClear
            placeholder="Search KLIPY"
            value={search}
            onChange={handleSearch}
          />
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={isCategoryView ? undefined : handleScroll}
        className="h-[320px] overflow-y-auto mt-2"
      >
        {loading ? (
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: PER_PAGE }).map((_, index) => (
              <Skeleton.Button
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                active
                block
                style={{ height: 100 }}
              />
            ))}
          </div>
        ) : isCategoryView ? (
          <div className="grid grid-cols-2 gap-2">
            {/* Trending tile, mirroring the Tenor-style "Trending GIFs" entry. */}
            <button
              type="button"
              className="relative p-0 border-0 bg-grey-100 cursor-pointer rounded overflow-hidden h-[100px]"
              onClick={() => openCategory("Trending GIFs", TRENDING)}
            >
              {categories[0]?.preview_url && (
                <img
                  src={categories[0].preview_url}
                  alt="Trending GIFs"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              )}
              <span className="absolute inset-0 flex items-center justify-center text-white font-semibold bg-black/30">
                Trending GIFs
              </span>
            </button>
            {categories.map(cat => (
              <button
                key={cat.category}
                type="button"
                className="relative p-0 border-0 bg-grey-100 cursor-pointer rounded overflow-hidden h-[100px]"
                onClick={() => openCategory(cat.category, cat.query)}
              >
                <img
                  src={cat.preview_url}
                  alt={cat.category}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <span className="absolute inset-0 flex items-center justify-center text-white font-semibold capitalize bg-black/30">
                  {cat.category}
                </span>
              </button>
            ))}
          </div>
        ) : gifs.length === 0 && !loading ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No GIFs found" />
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {gifs.map(gif => {
              // Use the lighter webp rendition for the grid thumbnail (the
              // full gif is still sent on select). Fall back to gif if webp
              // is unavailable.
              const thumb =
                gif?.file?.sm?.webp?.url ||
                gif?.file?.xs?.webp?.url ||
                gif?.file?.sm?.gif?.url ||
                gif?.file?.xs?.gif?.url ||
                gif?.file?.md?.gif?.url;
              return (
                <button
                  key={gif.id}
                  type="button"
                  className="p-0 border-0 bg-transparent cursor-pointer"
                  onClick={() => createMessage(gif)}
                >
                  <img
                    src={thumb}
                    alt={gif.title || "gif"}
                    loading="lazy"
                    className="w-full rounded object-cover"
                  />
                </button>
              );
            })}
            {loadingMore && (
              <div className="col-span-2 flex justify-center py-2">
                <Skeleton.Button active block style={{ height: 32 }} />
              </div>
            )}
          </div>
        )}
      </div>
      {/* Klipy attribution (required per https://docs.klipy.com/attribution).
          The logo already reads "Powered by KLIPY". */}
      <div className="flex items-center justify-center mt-1">
        <img src={KlipyLogo} alt="Powered by KLIPY" className="h-4" />
      </div>
    </div>
  );
};

export default GifPickerComponent;
